import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { util } from '../classes/util';
import { environment } from '../environment';
import { ESRI } from '../esri-loader-util';
import { ILayerColumn, ILayerSettings, IMapcoreGraphicsLayer, IMapcoreMapImageLayer, IMapcoreProps, IMapcoreSubLayer, IQuery, ISearchCriteria, isQueryableMapCoreLayer, LogLevel, MapCoreLayer, QueryableMapCoreLayer } from '../types';

import { FilterService } from './filter.service';
import { GeocodeService } from './geocode.service';
import { GisToolsService } from './gis-tools.service';
import { ILoggingService } from './interfaces/ILoggingService';
import { LayerService } from './layer.service';
import { MapService } from './map.service';
import { SpatialQueryService } from './spatialQuery.service';
import { ToSqlStringService } from './to-sql-string.service';

@Injectable({
    providedIn: 'root'
})
export class AoiService {
    aoiQueryName: string = 'AOI';
    columnIdentifiers: any;
    selectedMap: any;
    mapView: __esri.MapView;
    searchCriterias: ISearchCriteria[];
    graphicsLayer: IMapcoreGraphicsLayer;

    spatialQueryStatus: Subject<string> = new Subject<string>();

    public boundarySymbol: __esri.SimpleFillSymbolProperties = {
        color: [255, 255, 255, 0.1],
        style: 'none',
        outline: {
            color: [0, 0, 0],
            style: 'solid',
            width: 2
        }
    };
    public lineSymbol: __esri.SimpleLineSymbolProperties;

    public readonly openStreetViewEvent: EventEmitter<any> = new EventEmitter();

    constructor(private readonly layerService: LayerService,
        private readonly geocodeService: GeocodeService,
        private readonly gisToolsService: GisToolsService,
        private readonly mapService: MapService,
        private readonly spatialQueryService: SpatialQueryService,
        private readonly filterService: FilterService,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        private readonly toSqlStringService: ToSqlStringService) { }

    private async loadColumnIdentifiers() {
        if (!this.columnIdentifiers) {
            await this.gisToolsService.getAllColumnIdentifiers('name, id').then(result => {
                this.columnIdentifiers = result;
            });
        }
    }

    public getSearchCriterias() {
        return this.searchCriterias;
    }

    public setSearchCriterias(searchCriterias) {
        this.searchCriterias = this.cloneDeep(searchCriterias);
    }

    public clearSearchCriterias() {
        this.searchCriterias = [];
    }

    private cloneDeep(arr: any[]) {
        if (!arr) { return arr; }
        const retVal: any[] = [];
        for (const val of arr) {
            if (Array.isArray(val)) {
                retVal.push(this.cloneDeep(val));
            } else if (typeof val === 'object') {
                retVal.push(this.cloneDeepObj(val));
            } else {
                retVal.push(val);
            }
        }
        return retVal;
    }

    private cloneDeepObj(obj: { [key: string]: any }) {
        if (!obj) { return obj; }
        const retVal: { [key: string]: any } = {};
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (Array.isArray(val)) {
                retVal[key] = this.cloneDeep(val);
            } else if (typeof val === 'object') {
                retVal[key] = this.cloneDeepObj(val);
            } else {
                retVal[key] = val;
            }
        }
        return retVal;
    }

    private async showAOIByAddress(address: string, magicKey: string, isAoi?: boolean) {
        return new Promise<void>(resolve => {
            this.geocodeService.find(address, magicKey).subscribe(async result => {
                if (result.candidates.length > 0) {
                    const Graphic = await ESRI.Graphic;
                    const res = result.candidates[0];

                    const point = {
                        type: 'point',
                        x: res.location.x,
                        y: res.location.y,
                        spatialReference: result.spatialReference
                    };

                    const pointGraphic = new Graphic({
                        geometry: point,
                        symbol: {
                            type: 'simple-marker',
                            style: 'circle',
                            color: 'yellow',
                            size: '16px'
                        } as any
                    });
                    await this.addGraphicAndZoomToIt(pointGraphic, `AOI - ${address}`, isAoi);
                }
                resolve();
            });
        });
    }

    private async showAOIByLatLong(latitude, longitude, isAoi?: boolean) {
        const Graphic = await ESRI.Graphic;
        const pointGraphic = new Graphic({
            geometry: {
                type: 'point',
                latitude,
                longitude
            } as any,
            symbol: {
                type: 'simple-marker',
                style: 'circle',
                color: 'yellow',
                size: '16px'
            } as any
        });
        await this.addGraphicAndZoomToIt(pointGraphic, 'AOI - Lat/Lon Point', isAoi);
    }

    private async addGraphicAndZoomToIt(pointGraphic: any, title: string, isAoi?: boolean) {
        if (!this.graphicsLayer || !(<any>this.graphicsLayer).parent) {
            this.graphicsLayer = await this.layerService.addGraphicsLayer({ title }, { isAOI: isAoi });
        }
        this.graphicsLayer.add(pointGraphic);
        this.layerService.zoomToGraphic(pointGraphic);
    }

    private async processSearchCriterias(isAoi?: boolean) {
        if (this.searchCriterias && this.searchCriterias.length > 0) {
            switch (this.searchCriterias[0].type) {
                case 'state':
                    return;
                    break;

                case 'address':
                    await this.showAOIByAddress(this.searchCriterias[0].address, this.searchCriterias[0].magicKey, isAoi);
                    return;
                    break;

                case 'latLong':
                    await this.showAOIByLatLong(this.searchCriterias[0].latitude, this.searchCriterias[0].longitude, isAoi);
                    return;
                    break;

                default:
                    break;
            }
        }

        const result: IResult = {
            title: null,
            query: { operator: 'OR' }
        };

        if (this.searchCriterias && this.searchCriterias.length > 0) {
            //Merge Counties or Districts
            const countySearchCriteria = this.searchCriterias.filter(sc => sc.type === 'county');
            if (countySearchCriteria.length > 1) {
                this.searchCriterias = util.removeFromArray(this.searchCriterias, countySearchCriteria);
                const newCountySearchCriteria = { ...countySearchCriteria[0] };
                newCountySearchCriteria.countyCode = undefined;
                newCountySearchCriteria.countyCodes = countySearchCriteria.map(csc => csc.countyCode);
                this.searchCriterias.push(newCountySearchCriteria);
            }

            const districtSearchCriteria = this.searchCriterias.filter(sc => sc.type === 'district');
            if (districtSearchCriteria.length > 1) {
                this.searchCriterias = util.removeFromArray(this.searchCriterias, districtSearchCriteria);
                const newDistrictSearchCriteria = { ...districtSearchCriteria[0] };
                newDistrictSearchCriteria.countyCode = undefined;
                newDistrictSearchCriteria.countyCodes = districtSearchCriteria.map(csc => csc.countyCode);
                this.searchCriterias.push(newDistrictSearchCriteria);
            }
        }

        result.query.subqueries = this.searchCriterias.map(searchCriteria => {
            switch (searchCriteria.type) {
                case 'district':
                    return this.districtsSetup(result, searchCriteria);
                case 'county':
                    return this.countiesSetup(result, searchCriteria);
                case 'zipCode':
                    result.layerId = environment.layers.find(l => l.name === 'zipCode').id;
                    result.title = `ZipCode: ${searchCriteria.zip}`;
                    return {
                        field: 'zip',
                        operator: '=',
                        value: searchCriteria.zip
                    };
                case 'municipality':
                    result.layerId = environment.layers.find(l => l.name === 'municipality').id;
                    result.title = `County: ${searchCriteria.countyCode} Municipality: ${searchCriteria.municipalityCode}`;
                    return {
                        operator: 'AND',
                        subqueries: [
                            { field: 'county', operator: '=', value: searchCriteria.countyCode },
                            { field: 'municipal_code', operator: '=', value: searchCriteria.municipalityCode }
                        ]
                    };
                case 'schoolDistrict':
                    result.layerId = environment.layers.find(l => l.name === 'schoolDistrict').id;
                    result.title = `County: ${searchCriteria.countyCode} schoolDistrict: ${searchCriteria.schoolDistrictName}`;
                    return {
                        operator: 'AND',
                        subqueries: [
                            { field: 'PA_CTY_CODE', operator: '=', value: searchCriteria.countyCode },
                            { field: 'SCHOOL_DISTRICT', operator: '=', value: searchCriteria.schoolDistrictName }
                        ]
                    };
                case 'planningPartner':
                    result.layerId = environment.layers.find(l => l.name === 'planningPartner').id;
                    result.title = `Planning Partner: ${searchCriteria.planningPartnerShortName}`;
                    return {
                        field: 'PLN_PRTNR_SHRTNM',
                        operator: '=',
                        value: searchCriteria.planningPartnerShortName
                    };
                case 'houseDistrict':
                    result.layerId = environment.layers.find(l => l.name === 'houseDistrict').id;
                    result.title = `House District: ${searchCriteria.houseDistrictNumber}`;
                    return {
                        field: 'LEG_DISTRICT_NO',
                        operator: '=',
                        value: searchCriteria.houseDistrictNumber.toString()
                    };
                case 'senateDistrict':
                    result.layerId = environment.layers.find(l => l.name === 'senateDistrict').id;
                    result.title = `Senate District: ${searchCriteria.senateDistrictNumber}`;
                    return {
                        field: 'LEG_DISTRICT_NO',
                        operator: '=',
                        value: searchCriteria.senateDistrictNumber.toString()
                    };
                case 'congressionalDistrict':
                    result.layerId = environment.layers.find(l => l.name === 'congressDistrict').id;
                    result.title = `Congressional District: ${searchCriteria.congressionalDistrictNumber}`;
                    return {
                        field: 'LEG_DISTRICT_NO',
                        operator: '=',
                        value: searchCriteria.congressionalDistrictNumber.toString()
                    };
                case 'route':
                    result.layerId = environment.layers.find(l => l.name === 'rmsseg').id;

                    let components = [searchCriteria.countyCode, searchCriteria.stateRouteNumber, searchCriteria.segBgn, searchCriteria.segEnd, searchCriteria.offset];
                    components = components.filter(x => x);
                    if (components.length > 1) {
                        result.title = components.join('|');
                    }
                    const subqueries = this.constructRouteSubqueries(searchCriteria);
                    return {
                        operator: 'AND',
                        subqueries
                    };
                case 'assetId':
                    if (searchCriteria) {
                        result.layerId = searchCriteria.layerId;
                        if (searchCriteria.data && searchCriteria.columnName) {
                            let operator = '=';

                            if (searchCriteria && searchCriteria.data.split(',').length > 1) {
                                operator = 'IN';
                            }
                            return {
                                field: searchCriteria.columnName,
                                operator,
                                value: searchCriteria.data
                            };
                        } else if (searchCriteria.query) {
                            return searchCriteria.query;
                        }
                    } else {
                        return null;
                    }
                    break;
                case 'intersection':
                    result.layerId = environment.layers.find(l => l.name === 'intersection').id;
                    return searchCriteria.query;
                case 'tsmo':
                    result.layerId = environment.layers.find(l => l.name === 'district').id;
                    result.title = `TSMO District${searchCriteria.districtNumbers.length > 1 ? 's' : ''}: ${searchCriteria.districtNumbers.join(',')}`;
                    return {
                        field: 'DISTRICT_NUMBER',
                        operator: 'IN',
                        value: searchCriteria.districtNumbers.join(',')
                    };
                default:
                    break;
            }
        });

        if (result.query.subqueries) {
            result.query.subqueries = result.query.subqueries.filter(x => x !== undefined);
        }

        if (!result.title) {
            const titles = this.searchCriterias.map(s => s.title).filter(x => x);
            if (titles.length) {
                result.title = 'AOI - ' + titles.join(', ');
            }
        }

        return result;
    }

    private districtsSetup(result: IResult, searchCriteria: ISearchCriteria) {
        result.layerId = environment.layers.find(l => l.name === 'district').id;
        const districts = searchCriteria.districtNumbers || [searchCriteria.districtNumber];
        result.title = `District Number${districts.length > 1 ? 's' : ''}: ${districts.join(', ')}`;
        return {
            field: 'DISTRICT_NUMBER',
            operator: districts.length > 1 ? 'IN' : '=',
            value: districts.join(',')
        };
    }

    private countiesSetup(result: IResult, searchCriteria: ISearchCriteria) {
        result.layerId = environment.layers.find(l => l.name === 'county').id;
        const counties = searchCriteria.countyCodes || [searchCriteria.countyCode];
        result.title = `Count${counties.length > 1 ? 'ies' : 'y'}: ${counties.join(', ')}`;
        return {
            field: 'COUNTY_NUMBER',
            operator: counties.length > 1 ? 'IN' : '=',
            value: counties.join(',')
        };
    }

    private constructDistrictSubquery(searchCriteria: ISearchCriteria, districtColumn: ILayerColumn) {
        return {
            field: districtColumn.name,
            operator: searchCriteria.districtNumbers ? 'IN' : '=',
            value: searchCriteria.districtNumber || searchCriteria.districtNumbers.join(',')
        };
    }

    private constructCountySubquery(searchCriteria: ISearchCriteria, countyColumn: ILayerColumn) {
        return {
            field: countyColumn.name,
            operator: searchCriteria.countyCodes ? 'IN' : '=',
            value: searchCriteria.countyCode || searchCriteria.countyCodes.join(',')
        };
    }

    private constructRouteSubqueries(searchCriteria: ISearchCriteria) {
        let subqueries: IQuery[] = [];
        if (searchCriteria.countyCode) {
            subqueries = subqueries.concat([{ field: 'CTY_CODE', operator: '=', value: searchCriteria.countyCode }]);
        }
        if (searchCriteria.stateRouteNumber) {
            subqueries = subqueries.concat([{ field: 'ST_RT_NO', operator: '=', value: searchCriteria.stateRouteNumber }]);
        }
        if (searchCriteria.segBgn && searchCriteria.segEnd) {
            subqueries = subqueries.concat([
                { field: 'SEG_NO', operator: '>=', value: searchCriteria.segBgn },
                { field: 'SEG_NO', operator: '<=', value: searchCriteria.segEnd }
            ]);
        } else if (searchCriteria.segBgn) {
            subqueries = subqueries.concat([{ field: 'SEG_NO', operator: '=', value: searchCriteria.segBgn }]);
        }
        return subqueries;
    }

    private getDistrictColumn(columns: ILayerColumn[]) {
        return columns.find(c => c.columnIdentifierId === this.columnIdentifiers.find(i => i.name === 'districtNumber').id);
    }

    private getCountyColumn(columns: ILayerColumn[]) {
        return columns.find(c => c.columnIdentifierId === this.columnIdentifiers.find(i => i.name === 'countyCode').id);
    }

    private async generateFilterSubqueries(mapcoreProps: IMapcoreProps, columns?: ILayerColumn[]) {
        if (!mapcoreProps.parentId || !this.searchCriterias) {
            return [];
        }

        if (this.searchCriterias && this.searchCriterias.length > 0 && this.searchCriterias[0].type === 'state') {
            return;
        }

        await this.loadColumnIdentifiers();

        if (!columns) {
            columns = mapcoreProps.columns;
            if (!columns || columns.length === 0) {
                columns = await this.gisToolsService.getColumns(mapcoreProps.parentId, 'name, columnIdentifierId');
            }
        }

        const subqueries = await Promise.all(this.searchCriterias.map(async searchCriteria => {
            switch (searchCriteria.type) {
                case 'district':
                    const districtColumn = this.getDistrictColumn(columns);
                    if (districtColumn) {
                        return this.constructDistrictSubquery(searchCriteria, districtColumn);
                    }

                    const county = this.getCountyColumn(columns);
                    if (county) {
                        const counties = await this.gisToolsService.getCountiesByDistricts([searchCriteria.districtNumber]);
                        return {
                            field: county.name,
                            operator: 'IN',
                            value: counties.map(c => c.countyCode).toString()
                        };
                    }
                    break;

                case 'county':
                    const countyColumn = this.getCountyColumn(columns);
                    if (countyColumn) {
                        return this.constructCountySubquery(searchCriteria, countyColumn);
                    }
                    break;

                case 'municipality':
                    return this.generateMunicipalityFilterSubqueries(columns, searchCriteria);
                    break;

                case 'planningPartner':
                    const planningPartnerColumnIdentifier = this.columnIdentifiers.find(i => i.name === 'planningPartnerShortName').id;
                    const planningPartnerColumn = columns.find(c => c.columnIdentifierId === planningPartnerColumnIdentifier);
                    if (planningPartnerColumn) {
                        return {
                            field: planningPartnerColumn.name,
                            operator: '=',
                            value: searchCriteria.planningPartnerShortName
                        };
                    }
                    break;

                case 'houseDistrict':
                    const houseColumnIdentifier = this.columnIdentifiers.find(i => i.name === 'houseDistrictNumber').id;

                    const houseColumn = columns.find(c => c.columnIdentifierId === houseColumnIdentifier);
                    if (houseColumn) {
                        return {
                            field: houseColumn.name,
                            operator: 'IN',
                            value: searchCriteria.houseDistrictNumber.toString()
                        };
                    }

                    break;

                case 'senateDistrict':
                    const senateColumnIdentifier = this.columnIdentifiers.find(i => i.name === 'senateDistrictNumber').id;
                    const senateColumn = columns.find(c => c.columnIdentifierId === senateColumnIdentifier);
                    if (senateColumn) {
                        return {
                            field: senateColumn.name,
                            operator: 'IN',
                            value: searchCriteria.senateDistrictNumber.toString()
                        };
                    }

                    break;

                case 'congressionalDistrict':
                    const congressColumnIdentifier = this.columnIdentifiers.find(i => i.name === 'congressDistrictNumber').id;
                    const congressColumn = columns.find(c => c.columnIdentifierId === congressColumnIdentifier);
                    if (congressColumn) {
                        return {
                            field: congressColumn.name,
                            operator: 'IN',
                            value: searchCriteria.congressionalDistrictNumber.toString()
                        };
                    }
                    break;

                case 'route':
                    return this.generateRouteFilterSubqueries(columns, searchCriteria);
                    break;
                case 'intersection':
                    const countyQuery = { ...searchCriteria.query.subqueries.filter(x => x.field === 'cty_code')[0] };
                    countyQuery.name = this.aoiQueryName;
                    return countyQuery;
                    break;

                case 'tsmo':
                    const districtIdentifier = this.columnIdentifiers.find(i => i.name === 'districtNumber').id;
                    const district = columns.find(c => c.columnIdentifierId === districtIdentifier);
                    const ctyIdentifier = this.columnIdentifiers.find(i => i.name === 'countyCode').id;
                    const cty = columns.find(c => c.columnIdentifierId === ctyIdentifier);
                    if (district) {
                        return {
                            field: district.name,
                            operator: 'IN',
                            value: searchCriteria.districtNumbers.join(',')
                        };
                    }

                    if (cty) {
                        const counties = await Promise.all(searchCriteria.districtNumbers.map(d => this.gisToolsService.getCountiesByDistricts([d])));
                        return {
                            field: cty.name,
                            operator: 'IN',
                            value: counties.flat().map(c => c.countyCode).toString()
                        };
                    }
                    break;

                default:
                    break;
            }

        }));
        return subqueries.filter(q => q !== undefined);
    }

    private generateMunicipalityFilterSubqueries(columns, searchCriteria) {
        const county = columns.find(c => c.columnIdentifierId === this.columnIdentifiers.find(i => i.name === 'countyCode').id);
        const municipality = columns.find(c => c.columnIdentifierId === this.columnIdentifiers.find(i => i.name === 'municipalityCode').id);
        const countyMunicipality = columns.find(c => c.columnIdentifierId === this.columnIdentifiers.find(i => i.name === 'countyMunicipalityCode').id);

        if (county && municipality) {
            return {
                operator: 'AND',
                subqueries: [
                    { field: county.name, operator: '=', value: searchCriteria.countyCode },
                    { field: municipality.name, operator: 'INCLUDES', value: searchCriteria.municipalityCode }
                ]
            };
        }

        if (countyMunicipality) {
            return {
                operator: 'AND',
                subqueries: [
                    { field: countyMunicipality.name, operator: 'INCLUDES', value: `${searchCriteria.countyCode}${searchCriteria.municipalityCode}` }
                ]
            };
        }
    }

    private generateRouteFilterSubqueries(columns, searchCriteria) {
        const countyCode = columns.find(c => c.columnIdentifierId === this.columnIdentifiers.find(i => i.name === 'countyCode').id);
        const routeNumber = columns.find(c => c.columnIdentifierId === this.columnIdentifiers.find(i => i.name === 'routeNumber').id);

        let subqueries;
        if (countyCode && routeNumber) {
            subqueries = [
                { field: countyCode.name, operator: '=', value: searchCriteria.countyCode },
                { field: routeNumber.name, operator: '=', value: searchCriteria.stateRouteNumber }
            ];
        }

        if (subqueries) {
            return { operator: 'AND', subqueries };
        }
    }

    public async showBySearchCriterias(addAsAOI = true, zoomToLayer = true, filter = true, clear = true, isGlobalFilter = false) {
        try {
            if (clear) {
                this.layerService.removeAoiLayers();
            }
            const result = await this.processSearchCriterias(addAsAOI);

            if (result) {
                let symbols;
                if (this.searchCriterias[0].type !== 'assetId' && this.searchCriterias[0].type !== 'route' && this.searchCriterias[0].type !== 'intersection') {
                    this.boundarySymbol.type = 'simple-fill';
                    if (this.boundarySymbol.outline) {
                        this.boundarySymbol.outline.type = 'simple-line';
                    }
                    symbols = [this.boundarySymbol];
                } else if (this.lineSymbol) {
                    symbols = [this.lineSymbol];
                }
                const layerSetting: ILayerSettings = {
                    id: result.layerId,
                    name: result.title,
                    query: result.query,
                    isAOI: addAsAOI,
                    opacity: 1,
                    renderers: symbols,
                    identifyEnabled: true,
                    zoomToLayer,
                    generateNewLayer: true
                };

                if (this.searchCriterias[0].type === 'assetId') {
                    layerSetting.identifyEnabled = true;
                }

                const layers: any[] = await this.layerService.addMapcoreLayersToMap([layerSetting]);
                layers.forEach(l => this.layerService.moveLayerToTheBottom(l));
            }

            const aoiLayers = this.layerService.getAoiLayers();
            const polygonLayers = aoiLayers.filter(x => x.mapcoreProps && x.mapcoreProps.geometryTypes.indexOf('polygon') >= 0);
            polygonLayers.forEach(l => this.layerService.moveLayerToTheBottom(l));

            return filter ? await this.filterLayers(null, isGlobalFilter) : null;
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        }
    }

    public async showLayersFromQueryParameters(removeAoiLayers, zoomToLayer, color) {
        let mapcoreLayer: IMapcoreSubLayer;

        try {
            if (removeAoiLayers) {
                this.layerService.removeAoiLayers();
            }

            const result = await this.processSearchCriterias(true);

            if (result) {
                const layerSetting: ILayerSettings = {
                    id: result.layerId,
                    name: result.title,
                    query: result.query,
                    isAOI: true,
                    zoomToLayer,
                    generateNewLayer: true,
                    visible: false // Initially hide to ensure default color isn't displayed
                };

                const esriLayers: MapCoreLayer[] = await this.layerService.addMapcoreLayersToMap([layerSetting]);
                const esriMapImageLayer = esriLayers[0] as IMapcoreMapImageLayer;
                mapcoreLayer = (esriMapImageLayer.sublayers as __esri.Collection<IMapcoreSubLayer>).toArray()[0];
                const esriLayerRenderer = mapcoreLayer.renderer as __esri.Renderer;
                (esriLayerRenderer as any).symbol.color = color;
                esriMapImageLayer.visible = true; // Now display with desired color
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        }

        return mapcoreLayer;
    }

    public async generateFilterQuery(mapcoreProps: IMapcoreProps) {
        const filterSubqueries = await this.generateFilterSubqueries(mapcoreProps);
        const filterQuery = this.getFilterQuery(filterSubqueries);
        return filterQuery;
    }

    getFilterQuery(filterSubqueries): IQuery {
        let filterQuery: IQuery = {};
        if (filterSubqueries) {
            if (filterSubqueries.length > 1) {
                filterQuery = { operator: 'OR', subqueries: filterSubqueries };
            } else if (filterSubqueries.length === 1) {
                filterQuery = filterSubqueries[0];
            }
            filterQuery.name = this.aoiQueryName;
        }
        return filterQuery;
    }

    public async filterLayers(specificLayers?: number[], isGlobalFilter: boolean = false) {
        const filteredLayers = [];
        const unfilteredLayers = [];
        const layers = this.getAllLayers(specificLayers);
        await this.loadColumnIdentifiers();
        await Promise.all(layers.map(async (layer) => {
            if (layer.mapcoreProps && layer.mapcoreProps.parentId && !layer.mapcoreProps.isAOI && isQueryableMapCoreLayer(layer)) {
                const aoiQuery = await this.generateFilterSubqueries(layer.mapcoreProps);
                if (aoiQuery) {
                    if (aoiQuery.length) {
                        if (!this.searchCriterias.find(c => c.type === 'county') && this.searchCriterias.find(c => c.type === 'district')) {
                            // Remove Disticts from existing query
                            await this.removeConflictingQuery('district', layer, false);
                        } else if (this.searchCriterias.find(c => c.type === 'county') && !this.searchCriterias.find(c => c.type === 'district')) {
                            // Remove County from existing query
                            await this.removeConflictingQuery('county', layer, false);
                            if (isGlobalFilter) {
                                await this.removeConflictingQuery('district', layer, false);
                            }
                        }
                        this.addQueryToLayer(aoiQuery, layer, true);
                        filteredLayers.push(layer);
                    } else {
                        unfilteredLayers.push(layer);
                    }
                } else if (this.searchCriterias[0].type === 'state') {
                    this.clearAOIFilter(layer);
                    filteredLayers.push(layer);
                }
            }
            // TODO: External layers
        }));
        if (unfilteredLayers.length) {
            const visibleLayers = [];
            const aoiPolygonLayers = this.layerService.getAoiLayers().filter(x => x.mapcoreProps && x.mapcoreProps.geometryTypes.indexOf('polygon') >= 0);
            if (aoiPolygonLayers.length) {
                unfilteredLayers.forEach(layer => {
                    if (layer.visible) {
                        layer.visible = false; // Prevent refresh until new AOI filter applied
                        visibleLayers.push(layer);
                    }
                    this.clearAOIFilter(layer);
                });
                this.spatialQueryStatus.next('querying');
                let spatialQueries;
                try {
                    spatialQueries = await this.spatialQueryService.spatialQueryLayers(true, unfilteredLayers, aoiPolygonLayers);
                } catch (e) {
                    this.spatialQueryStatus.next('error');
                    throw e;
                }
                this.spatialQueryStatus.next('filtering');
                for (const spatialQuery of spatialQueries) {
                    const layer = unfilteredLayers.filter(x => x.id === spatialQuery.id)[0];
                    const aoiQuery = this.spatialQueryService.getGuidQuery(spatialQuery.guid, this.aoiQueryName);
                    this.addQueryToLayer([aoiQuery], layer);
                    filteredLayers.push(layer);
                }
                visibleLayers.forEach(l => l.visible = true); // Filter applied, allow refresh
                this.spatialQueryStatus.next('done');
            }
        }
        return filteredLayers;
    }

    public async clearAOIFilter(layer: QueryableMapCoreLayer) {
        let query = layer.mapcoreProps.query;
        query = this.toSqlStringService.removeClauses(query, { name: this.aoiQueryName });
        // Only sub query matching need to clear and keep the child table/Join table query when global filter is cleared for statewide.
        if (layer.mapcoreProps.query.childTableQuery && layer.mapcoreProps.query.childTableRecord) {
            query.childTableQuery = layer.mapcoreProps.query.childTableQuery;
            query.childTableRecord = layer.mapcoreProps.query.childTableRecord;
        }
        if (layer.mapcoreProps.query.joinTableQuery && layer.mapcoreProps.query.joinRecord) {
            query.joinTableQuery = layer.mapcoreProps.query.joinTableQuery;
            query.joinRecord = layer.mapcoreProps.query.joinRecord;
        }
        this.layerService.updateLayerFilter(layer, query);
    }

    private getAllLayers(specificLayers?: number[]) {
        return (<MapCoreLayer[]>Object.values(this.layerService.mapLayers).flat()).filter(layer => (!specificLayers || !specificLayers.length) || (specificLayers.includes(layer.mapcoreProps.id) || specificLayers.includes(layer.mapcoreProps.parentId)));
    }

    public addQueryToLayer(aoiQuery: IQuery[], layer: QueryableMapCoreLayer, updateLayerTheme: boolean = true) {
        if (aoiQuery.length > 0) {
            const filterQuery = this.getFilterQuery(aoiQuery);
            let query = layer.mapcoreProps.query;
            query = this.toSqlStringService.removeClauses(query, { name: this.aoiQueryName });
            query = this.toSqlStringService.addFilter(query, filterQuery);
            this.layerService.updateLayerFilter(layer, query, updateLayerTheme);
            this.filterService.saveLayerUsage(layer.mapcoreProps.id ? layer.mapcoreProps.id : layer.mapcoreProps.parentId, query);
            return layer;
        }
    }

    public async removeConflictingQuery(type: 'county' | 'district', layer: QueryableMapCoreLayer, updateLayerTheme: boolean = true) {
        if (!layer.mapcoreProps.columns || layer.mapcoreProps.columns.length === 0) {
            layer.mapcoreProps.columns = await this.gisToolsService.getColumns(layer.mapcoreProps.parentId, 'name, columnIdentifierId');
        }
        const columns = layer.mapcoreProps.columns;

        let filterColumnToRemove: ILayerColumn;
        switch (type) {
            case 'county':
                filterColumnToRemove = this.getCountyColumn(columns);
                break;
            case 'district':
                filterColumnToRemove = this.getDistrictColumn(columns);
                break;
            default:
                break;
        }
        if (filterColumnToRemove) {
            let query = layer.mapcoreProps.query;
            query = this.toSqlStringService.removeClauses(query, { field: filterColumnToRemove.name });
            if (layer.mapcoreProps.query.childTableQuery && layer.mapcoreProps.query.childTableRecord) {
                query.childTableQuery = layer.mapcoreProps.query.childTableQuery;
                query.childTableRecord = layer.mapcoreProps.query.childTableRecord;
            }
            if (layer.mapcoreProps.query.joinTableQuery && layer.mapcoreProps.query.joinRecord) {
                query.joinTableQuery = layer.mapcoreProps.query.joinTableQuery;
                query.joinRecord = layer.mapcoreProps.query.joinRecord;
            }
            this.layerService.updateLayerFilter(layer, query, updateLayerTheme);
        }
    }

    public async setSelectedMapId(mapId: number) {
        const response = await this.mapService.getMaps([mapId]);
        this.selectedMap = response[0];
    }

    public async showBySearchCriteriasAndMap(addAsAOI = true) {
        if (this.selectedMap) {
            await this.mapService.openMap(this.selectedMap);
        }
        return await this.showBySearchCriterias(addAsAOI);
    }

    public turnOnLayerIds(layerIds: number[]) {
        this.layerService.turnOnLayerIds(layerIds);
    }

    public async openStreetView(params: any) {
        let streetViewLocation = {
            countyCode: null,
            stateRouteNumber: null,
            segmentNumber: null,
            offset: 0
        };

        if (params.countyNumber && params.routeNumber && params.segmentNumber) {
            streetViewLocation.countyCode = params.countyNumber;
            streetViewLocation.stateRouteNumber = params.routeNumber;
            streetViewLocation.segmentNumber = params.segmentNumber;
        } else if (params.latitude && params.longitude) {
            streetViewLocation = await this.getLocationFromLatLng(params.latitude, params.longitude);
        } else if (params.crossingId) {
            const railCrossing = await this.gisToolsService.getRailCrossing(params.crossingId, 'crossingNumber, rmsLatitude, rmsLongitude');

            if (railCrossing && railCrossing.rmsLatitude && railCrossing.rmsLongitude) {
                streetViewLocation = await this.getLocationFromLatLng(railCrossing.rmsLatitude, railCrossing.rmsLongitude);
            }
        }

        if (streetViewLocation.countyCode !== null && streetViewLocation.stateRouteNumber !== null && streetViewLocation.segmentNumber !== null) {
            this.openStreetViewEvent.emit(streetViewLocation);
        }
    }

    private async getLocationFromLatLng(latitude: any, longitude: any, buffer: number = 100) {
        const crs = {
            countyCode: null,
            stateRouteNumber: null,
            segmentNumber: null,
            offset: 0
        };

        const location = await this.gisToolsService.getOffsetForLatLong(latitude, longitude, buffer, 'countyCode, stateRouteNumber, segmentNumber, offset');

        if (location && location.length > 0) {
            crs.countyCode = location[0].countyCode;
            crs.stateRouteNumber = location[0].stateRouteNumber;
            crs.segmentNumber = location[0].segmentNumber;
            crs.offset = location[0].offset;
        }

        return crs;
    }
}

interface IResult {
    title?: string;
    layerId?: number;
    query: IQuery;
}
